import type { ContactType, DXGGlobalParticipant, DXGAddress, DXGParticipantEmployment } from '@makemydeal/dr-platform-types';
import type { QualifiedPhoneNumbers } from '@makemydeal/dr-shared-types';
import type { DxgIndividualBuyer } from '../types/dxgTypes';

type PhoneBearingParticipant = Pick<DXGGlobalParticipant, 'phoneContactInfo' | 'additionalContacts'>;

const isCell = (contactType?: ContactType, isMobile?: boolean): boolean => contactType === 'Personal' && isMobile === true;
const isHome = (contactType?: ContactType, isMobile?: boolean): boolean => contactType === 'Personal' && isMobile === false;
const isWork = (contactType?: ContactType): boolean => contactType === 'Work';

export const getQualifiedPhoneNumbers = (participant?: PhoneBearingParticipant | null): QualifiedPhoneNumbers => {
    const qualified: QualifiedPhoneNumbers = { cellPhone: '', homePhone: '', workPhone: '' };

    if (!participant) return qualified;

    const primary = participant.phoneContactInfo;
    if (primary?.phoneNumber) {
        if (isCell(primary.contactType, primary.isMobile)) qualified.cellPhone = primary.phoneNumber;
        if (isHome(primary.contactType, primary.isMobile)) qualified.homePhone = primary.phoneNumber;
        if (isWork(primary.contactType)) qualified.workPhone = primary.phoneNumber;
    }

    participant.additionalContacts?.forEach((contact) => {
        if (!('phoneNumber' in contact) || !contact.phoneNumber) return;

        const { contactType, isMobile, phoneNumber } = contact;
        if (!qualified.cellPhone && isCell(contactType, isMobile)) qualified.cellPhone = phoneNumber;
        if (!qualified.homePhone && isHome(contactType, isMobile)) qualified.homePhone = phoneNumber;
        if (!qualified.workPhone && isWork(contactType)) qualified.workPhone = phoneNumber;
    });

    return qualified;
};

export const getParticipant = (
    buyer: DxgIndividualBuyer,
    participants: Array<DXGGlobalParticipant | undefined>,
    enableCorrectPhoneHandling?: boolean
): DXGGlobalParticipant | undefined => {
    const { commonConsumerId: buyerCommonConsumerId, firstName, lastName, emailAddress } = buyer;

    if (!participants) {
        return undefined;
    }

    const latestFirst = () => (enableCorrectPhoneHandling ? [...participants].reverse() : participants.reverse());

    if (buyerCommonConsumerId && buyerCommonConsumerId.length) {
        return latestFirst().find((participant) => participant?.commonConsumerId === buyerCommonConsumerId);
    }

    if (firstName && lastName && emailAddress) {
        return latestFirst().find(
            (participant) =>
                participant?.name?.firstName?.toLowerCase() === firstName.toLowerCase() &&
                participant?.name?.lastName?.toLowerCase() === lastName.toLowerCase() &&
                participant?.emailContactInfo?.emailAddress.toLowerCase() === emailAddress.toLowerCase()
        );
    }
    return undefined;
};

export const splitMonthsToYearsAndMonths = (totalMonths?: number): { years?: number; months?: number } => {
    if (typeof totalMonths !== 'number') return {};
    return {
        years: Math.floor(totalMonths / 12),
        months: totalMonths % 12
    };
};

export const toShopperAddress = (address?: DXGAddress) =>
    address && {
        ...(address.addressLine1 !== undefined && { streetAddress: address.addressLine1 }),
        ...(address.addressLine2 !== undefined && { streetAddress2: address.addressLine2 }),
        ...(address.addressLine3 !== undefined && { streetAddress3: address.addressLine3 }),
        ...(address.postalCode !== undefined && { zip: address.postalCode }),
        ...(address.city !== undefined && { city: address.city }),
        ...(address.countyDistrict !== undefined && { county: address.countyDistrict }),
        ...(address.stateProvince !== undefined && { state: address.stateProvince }),
        ...(address.country !== undefined && { country: address.country })
    };

export const toShopperEmployment = (employment: DXGParticipantEmployment) => {
    const { years: totalYearsEmployed, months: totalMonthsEmployed } = splitMonthsToYearsAndMonths(employment.totalMonthsEmployed);

    return {
        ...(employment.status !== undefined && { status: employment.status }),
        ...(employment.employerName !== undefined && { employerName: employment.employerName }),
        ...(employment.occupation !== undefined && { occupation: employment.occupation }),
        ...(employment.workPhone !== undefined && { workPhone: employment.workPhone }),
        ...(totalMonthsEmployed !== undefined && { totalMonthsEmployed }),
        ...(totalYearsEmployed !== undefined && { totalYearsEmployed }),
        ...(employment.employerAddress && { address: toShopperAddress(employment.employerAddress) })
    };
};
